// The wizard's steps: their order, their stable keys, and their titles.
//
// This is the single source of truth for what steps exist and what order they
// come in. Everything else that is "one entry per step" is a
// `Record<StepKey, …>` keyed off it — the step components, the backend stages,
// the header icons, the has-data probes — so adding a step becomes a compile
// error at every place that has to say something about it, rather than a
// renumbering that silently succeeds.
//
// That is the lesson of the bug this module was extracted for. "Channel
// Modifiers" was inserted at index 5 and every later step shifted by one;
// positional tables and `?step=N` deep links kept their old numbers and kept
// working, just pointing one step too early. A stale number is still a valid
// number. A missing key is not.
//
// Two properties here are load-bearing and easy to destroy by accident:
//
//   1. NO IMPORTS. `WizardStep` is derived below and re-exported from
//      utils/wizardInvalidation.ts, which around forty modules import —
//      including pure-logic leaves like wizardStepStatus.ts. Any import added
//      here is inherited by all of them, which is why the FontAwesome icons
//      live in SetupWizardPage.tsx beside the step components instead of here.
//   2. IMPORTABLE UNDER JEST. Importing SetupWizardPage pulls in all fourteen
//      step components, one of which transitively imports a stylesheet from
//      node_modules; this repo's `transformIgnorePatterns` replaces CRA's
//      default without carving CSS out, so Jest evaluates that stylesheet as
//      JavaScript and the import fails. Keeping this module component-free is
//      what lets a test ask what the steps are called.
//
// The `as const` is what makes `key` a literal union rather than `string`.
// Without it every `Record<StepKey, …>` silently widens to `Record<string, …>`
// and stops catching the thing it exists to catch.
export const WIZARD_STEPS = [
  { key: "Organization", title: "Organization" },
  { key: "Server", title: "Server" },
  { key: "Adapters", title: "Adapters" },
  { key: "Channels", title: "Channels" },
  { key: "Banks", title: "Banks" },
  { key: "ChannelModifiers", title: "Channel Modifiers" },
  { key: "Lifts", title: "Lifts & Floors" },
  { key: "Debounce", title: "Debounce" },
  { key: "Bacnet", title: "BACnet" },
  { key: "Users", title: "Users & Roles" },
  { key: "Notifications", title: "Notifications" },
  { key: "Actions", title: "Actions" },
  { key: "MonitorGrid", title: "Monitor Grid" },
  { key: "Review", title: "Review" },
] as const;

/** A step's stable identity — what every per-step table is keyed by. */
export type StepKey = (typeof WIZARD_STEPS)[number]["key"];

/**
 * Step index by name, derived from the list above rather than restated.
 *
 * This is what production code navigates by: `?step=${WizardStep.Lifts}` rather
 * than `?step=6`. It cannot disagree with WIZARD_STEPS, because it is computed
 * from it — inserting a step renumbers every member at once, which is the whole
 * point. utils/wizardInvalidation.ts, where this used to be hand-written,
 * re-exports it, so the modules importing it from there are unaffected.
 */
export const WizardStep = Object.fromEntries(
  WIZARD_STEPS.map((step, index) => [step.key, index]),
) as Record<StepKey, number>;
